import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Tabs,
  Tab,
  Divider,
  Avatar,
  Button,
  Grid,
  List,
  ListItem,
  ListItemText,
  Card,
  CardContent,
  CardActions,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Person as PersonIcon,
  ShoppingBag as ShoppingBagIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  RecordVoiceOver as RecordVoiceOverIcon,
  Store as StoreIcon,
  Message as MessageIcon,
  Home as HomeIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { getCustomerOrders } from '../../services/storeService';
import { logoutUser } from '../../services/authService';
import { getCustomerByEmail } from '../../services/customerService';
import { CustomerAddress } from '../../types/store';
import {
  getCustomerAddresses,
  addAddress,
  updateAddress,
  deleteAddress,
  setDefaultAddress
} from '../../services/addressService';
import { DEFAULT_EGYPT_SHIPPING_REGIONS } from '../../services/storeSettingsService';
// استخدام نوع بيانات أكثر مرونة للطلبات
interface OrderData {
  id: string;
  orderNumber?: string;
  customerId?: string;
  customerName?: string;
  customerPhone?: string;
  customerEmail?: string;
  items: any[];
  total?: number;
  subtotal?: number;
  shippingCost?: number;
  discount?: number;
  tax?: number;
  paymentMethod?: string;
  paymentStatus?: string;
  orderStatus?: string;
  shippingDetails?: any;
  notes?: string;
  createdAt: Date;
  updatedAt: Date;
  [key: string]: any; // للسماح بأي حقول إضافية
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`profile-tabpanel-${index}`}
      aria-labelledby={`profile-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
};

const ProfilePage: React.FC = () => {
  const { currentUser, setCurrentUser } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [orders, setOrders] = useState<OrderData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // التحقق من وجود مستخدم مسجل الدخول
    if (!currentUser) {
      navigate('/login');
      return;
    }

    // تحميل طلبات المستخدم
    const fetchOrders = async () => {
      try {
        setLoading(true);
        setError(null);
        console.log('Fetching orders for user:', currentUser.id);

        const userOrders = await getCustomerOrders(currentUser.id);
        console.log('Orders fetched successfully:', userOrders);

        if (Array.isArray(userOrders)) {
          // التأكد من أن كل طلب يحتوي على مصفوفة items
          const validOrders = userOrders.map(order => ({
            ...order,
            items: Array.isArray((order as any).items) ? (order as any).items : []
          }));
          setOrders(validOrders);
        } else {
          console.error('Unexpected response format:', userOrders);
          setOrders([]);
          setError('تنسيق استجابة غير متوقع من الخادم');
        }
      } catch (err) {
        console.error('Error fetching orders:', err);
        setOrders([]);
        setError('حدث خطأ أثناء تحميل الطلبات');
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [currentUser, navigate]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleLogout = async () => {
    try {
      await logoutUser();
      setCurrentUser(null);
      navigate('/');
    } catch (err) {
      console.error('Error logging out:', err);
      setError('حدث خطأ أثناء تسجيل الخروج');
    }
  };

  // ترجمة حالة الطلب
  const translateOrderStatus = (status?: string) => {
    if (!status) return 'غير معروف';

    switch (status) {
      case 'pending':
        return 'قيد الانتظار';
      case 'processing':
        return 'قيد المعالجة';
      case 'shipped':
        return 'تم الشحن';
      case 'delivered':
        return 'تم التوصيل';
      case 'cancelled':
        return 'ملغي';
      default:
        return status || 'غير معروف';
    }
  };

  // ترجمة حالة الدفع
  const translatePaymentStatus = (status?: string) => {
    if (!status) return 'غير معروف';

    switch (status) {
      case 'paid':
        return 'مدفوع';
      case 'pending':
        return 'قيد الانتظار';
      case 'failed':
        return 'فشل الدفع';
      case 'refunded':
        return 'تم رد المبلغ';
      default:
        return status || 'غير معروف';
    }
  };

  // تحديد لون حالة الطلب
  const getOrderStatusColor = (status?: string) => {
    if (!status) return 'default';

    switch (status) {
      case 'pending':
        return 'warning';
      case 'processing':
        return 'info';
      case 'shipped':
        return 'primary';
      case 'delivered':
        return 'success';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  // تحديد لون حالة الدفع
  const getPaymentStatusColor = (status?: string) => {
    if (!status) return 'default';

    switch (status) {
      case 'paid':
        return 'success';
      case 'pending':
        return 'warning';
      case 'failed':
        return 'error';
      case 'refunded':
        return 'info';
      default:
        return 'default';
    }
  };

  if (!currentUser) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 8 }}>
        <Alert severity="warning">
          يرجى تسجيل الدخول لعرض الملف الشخصي
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 8 }}>
      <Paper elevation={3} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        {/* رأس الصفحة */}
        <Box sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', p: 3, textAlign: 'center' }}>
          <Avatar
            sx={{ width: 100, height: 100, mx: 'auto', mb: 2, bgcolor: 'secondary.main' }}
          >
            {currentUser.name ? currentUser.name.charAt(0).toUpperCase() : <PersonIcon />}
          </Avatar>
          <Typography variant="h5" gutterBottom>
            {currentUser.name}
          </Typography>
          <Typography variant="body1">
            {currentUser.email}
          </Typography>
        </Box>

        {/* تبويبات الملف الشخصي */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="fullWidth"
            aria-label="profile tabs"
          >
            <Tab icon={<PersonIcon />} label="الملف الشخصي" />
            <Tab icon={<ShoppingBagIcon />} label="الطلبات" />
            <Tab icon={<HomeIcon />} label="عناويني" />
            <Tab icon={<RecordVoiceOverIcon />} label="اقتراحاتي" />
            <Tab icon={<SettingsIcon />} label="الإعدادات" />
          </Tabs>
        </Box>

        {/* محتوى التبويب: الملف الشخصي */}
        <TabPanel value={activeTab} index={0}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                معلومات الحساب
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <List>
                <ListItem>
                  <ListItemText primary="الاسم" secondary={currentUser.name} />
                </ListItem>
                <ListItem>
                  <ListItemText primary="البريد الإلكتروني" secondary={currentUser.email} />
                </ListItem>
                <ListItem>
                  <ListItemText primary="تاريخ الانضمام" secondary={
                    currentUser.createdAt instanceof Date
                      ? currentUser.createdAt.toLocaleDateString('ar-EG')
                      : new Date(currentUser.createdAt).toLocaleDateString('ar-EG')
                  } />
                </ListItem>
              </List>
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="outlined"
                color="error"
                startIcon={<LogoutIcon />}
                onClick={handleLogout}
                sx={{ mt: 2 }}
              >
                تسجيل الخروج
              </Button>
            </Grid>
          </Grid>
        </TabPanel>

        {/* محتوى التبويب: الطلبات */}
        <TabPanel value={activeTab} index={1}>
          <Typography variant="h6" gutterBottom>
            طلباتي
          </Typography>
          <Divider sx={{ mb: 2 }} />

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : orders.length === 0 ? (
            <Alert severity="info">
              لا توجد طلبات سابقة
            </Alert>
          ) : (
            <Grid container spacing={2}>
              {orders.map((order) => (
                <Grid item xs={12} key={order.id}>
                  <Card variant="outlined">
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="subtitle1">
                          طلب #{order.orderNumber || order.id.substring(0, 8)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {order.createdAt instanceof Date
                            ? order.createdAt.toLocaleDateString('ar-EG')
                            : order.createdAt && !isNaN(new Date(order.createdAt).getTime())
                              ? new Date(order.createdAt).toLocaleDateString('ar-EG')
                              : 'تاريخ غير متوفر'}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                        <Chip
                          label={translateOrderStatus(order.orderStatus)}
                          color={getOrderStatusColor(order.orderStatus) as any}
                          size="small"
                        />
                        <Chip
                          label={translatePaymentStatus(order.paymentStatus)}
                          color={getPaymentStatusColor(order.paymentStatus) as any}
                          size="small"
                        />
                      </Box>
                      <Typography variant="body2" gutterBottom>
                        عدد المنتجات: {order.items.length}
                      </Typography>
                      <Typography variant="body2" gutterBottom>
                        المجموع: {order.total ? order.total.toLocaleString('ar-EG') : '0'} ج.م
                      </Typography>
                      {order.shippingCost && order.shippingCost > 0 && (
                        <Typography variant="body2" gutterBottom>
                          تكلفة الشحن: {order.shippingCost.toLocaleString('ar-EG')} ج.م
                        </Typography>
                      )}
                      <Typography variant="subtitle2">
                        الإجمالي: {((order.total || 0) + (order.shippingCost || 0)).toLocaleString('ar-EG')} ج.م
                      </Typography>
                    </CardContent>
                    <CardActions>
                      <Button
                        size="small"
                        onClick={() => navigate(`/order/${order.id}`)}
                      >
                        عرض التفاصيل
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </TabPanel>

        {/* محتوى التبويب: عناويني */}
        <TabPanel value={activeTab} index={2}>
          <AddressManagementSection />
        </TabPanel>

        {/* محتوى التبويب: اقتراحاتي */}
        <TabPanel value={activeTab} index={3}>
           <MySuggestionsSection />
        </TabPanel>

        {/* محتوى التبويب: الإعدادات */}
        <TabPanel value={activeTab} index={4}>
          <Typography variant="h6" gutterBottom>
            إعدادات الحساب
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Alert severity="info" sx={{ mb: 2 }}>
            ميزة تعديل إعدادات الحساب قيد التطوير
          </Alert>
        </TabPanel>
      </Paper>
    </Container>
  );
};

const MySuggestionsSection: React.FC = () => {
    const { currentUser } = useAuth();
    const [suggestions, setSuggestions] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchUserSuggestions = async () => {
            if (!currentUser) return;
            try {
                setLoading(true);
                setError(null);
                
                // 1. محاولة البحث بالبريد الإلكتروني أولاً
                const qEmail = query(
                    collection(db, 'product_suggestions'),
                    where('email', '==', currentUser?.email || '')
                );
                const snapshotEmail = await getDocs(qEmail);
                let userSuggestions = snapshotEmail.docs.map(doc => ({ id: doc.id, ...doc.data() }));

                // 2. محاولة البحث بالهاتف إذا لم نجد نتائج
                if (userSuggestions.length === 0 && currentUser?.email) {
                    const customer = await getCustomerByEmail(currentUser.email);
                    if (customer && customer.phone) {
                        const qPhone = query(
                            collection(db, 'product_suggestions'),
                            where('phone', '==', customer.phone)
                        );
                        const snapshotPhone = await getDocs(qPhone);
                        userSuggestions = snapshotPhone.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                    }
                }

                // 3. محاولة الفلترة بالاسم إذا كنت أدمن وتجرب
                if (userSuggestions.length === 0) {
                    const qAll = query(collection(db, 'product_suggestions'));
                    const snapshotAll = await getDocs(qAll);
                    userSuggestions = snapshotAll.docs
                        .map(doc => ({ id: doc.id, ...doc.data() } as any))
                        .filter(item => 
                            item.email === currentUser?.email || 
                            (item.name && item.name.includes(currentUser?.name || ''))
                        );
                }

                // ترتيب النتائج يدوياً لتجنب مشاكل الفهارس (Indexes)
                (userSuggestions as any[]).sort((a, b) => {
                    const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
                    const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);
                    return dateB.getTime() - dateA.getTime();
                });

                setSuggestions(userSuggestions);
            } catch (err: any) {
                console.error('Error fetching user suggestions:', err);
                setError('حدث خطأ أثناء جلب الاقتراحات: ' + (err.message || ''));
            } finally {
                setLoading(false);
            }
        };
        fetchUserSuggestions();
    }, [currentUser]);

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>;

    return (
        <Box>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            
            {suggestions.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Alert severity="info" sx={{ mb: 2 }}>لا توجد اقتراحات مرسلة مسبقاً</Alert>
                    <Typography variant="caption" color="text.disabled">
                        DEBUG: User={currentUser?.email}, Name={currentUser?.name}
                    </Typography>
                </Box>
            ) : (
                <Grid container spacing={2}>
                    {suggestions.map((item) => (
                        <Grid item xs={12} key={item.id}>
                            <Card variant="outlined">
                                <CardContent>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                        <Typography variant="subtitle1" fontWeight="bold">{item.productName}</Typography>
                                        <Chip 
                                            label={item.status === 'new' ? 'قيد المراجعة' : item.status === 'reviewed' ? 'تم الرد' : 'مكتمل'} 
                                            color={item.status === 'new' ? 'warning' : 'success'} 
                                            size="small" 
                                        />
                                    </Box>
                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                        <strong>اقتراحي:</strong> {item.suggestion}
                                    </Typography>
                                    
                                    {item.adminReply && (
                                        <Box sx={{ mt: 2, p: 2, bgcolor: 'primary.light', color: 'primary.contrastText', borderRadius: 1 }}>
                                            <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center' }}>
                                                <MessageIcon sx={{ mr: 0.5, fontSize: 18 }} /> رد الإدارة:
                                            </Typography>
                                            <Typography variant="body2">
                                                {item.adminReply}
                                            </Typography>
                                        </Box>
                                    )}
                                    
                                    <Typography variant="caption" sx={{ mt: 1, display: 'block', color: 'text.disabled', textAlign: 'left' }}>
                                        تم الإرسال في: {item.createdAt?.toDate ? item.createdAt.toDate().toLocaleString('ar-EG') : 'غير معروف'}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            )}
        </Box>
    );
};

const AddressManagementSection: React.FC = () => {
  const { currentUser } = useAuth();
  const [addresses, setAddresses] = useState<CustomerAddress[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modal Dialog State
  const [openDialog, setOpenDialog] = useState(false);
  const [editingAddress, setEditingAddress] = useState<CustomerAddress | null>(null);
  
  // Form State
  const [label, setLabel] = useState('');
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [addressText, setAddressText] = useState('');
  const [building, setBuilding] = useState('');
  const [floor, setFloor] = useState('');
  const [apartment, setApartment] = useState('');
  const [city, setCity] = useState('');
  const [region, setRegion] = useState('');

  const fetchAddresses = async () => {
    if (!currentUser) return;
    try {
      setLoading(true);
      setError(null);
      const data = await getCustomerAddresses(currentUser.id);
      setAddresses(data);
    } catch (err: any) {
      console.error('Error fetching addresses:', err);
      setError('حدث خطأ أثناء تحميل العناوين');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAddresses();
  }, [currentUser]);

  // When city changes, auto-register the shipping zone
  const handleCityChange = (selectedCity: string) => {
    setCity(selectedCity);
    const matchedRegion = DEFAULT_EGYPT_SHIPPING_REGIONS.find(r => 
      r.name === selectedCity || (r.cities && r.cities.some(c => c.name === selectedCity))
    );
    if (matchedRegion) {
      setRegion(matchedRegion.id); // Save region ID automatically
    } else {
      setRegion('');
    }
  };

  const handleOpenAdd = () => {
    setEditingAddress(null);
    setLabel('المنزل');
    setFullName(currentUser?.name || '');
    setPhone('');
    setEmail(currentUser?.email || '');
    setAddressText('');
    setBuilding('');
    setFloor('');
    setApartment('');
    setCity('');
    setRegion('');
    setOpenDialog(true);
  };

  const handleOpenEdit = (addr: CustomerAddress) => {
    setEditingAddress(addr);
    setLabel(addr.label);
    setFullName(addr.fullName);
    setPhone(addr.phone);
    setEmail(addr.email || '');
    setAddressText(addr.address);
    setBuilding(addr.building || '');
    setFloor(addr.floor || '');
    setApartment(addr.apartment || '');
    setCity(addr.city);
    setRegion(addr.region);
    setOpenDialog(true);
  };

  const handleSave = async () => {
    if (!currentUser) return;
    if (!label.trim() || !fullName.trim() || !phone.trim() || !addressText.trim() || !city) {
      setError('يرجى ملء جميع الحقول الإلزامية');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Determine region automatically if not set
      let finalRegion = region;
      if (!finalRegion) {
        const matchedRegion = DEFAULT_EGYPT_SHIPPING_REGIONS.find(r => 
          r.name === city || (r.cities && r.cities.some(c => c.name === city))
        );
        if (matchedRegion) {
          finalRegion = matchedRegion.id;
        }
      }

      const addressData = {
        label,
        fullName,
        phone,
        email,
        address: addressText,
        building,
        floor,
        apartment,
        city,
        region: finalRegion,
        isDefault: editingAddress ? editingAddress.isDefault : addresses.length === 0, // set default if first address
      };

      if (editingAddress) {
        await updateAddress(currentUser.id, {
          ...editingAddress,
          ...addressData,
          updatedAt: new Date()
        } as any);
      } else {
        await addAddress(currentUser.id, addressData);
      }

      setOpenDialog(false);
      await fetchAddresses();
    } catch (err: any) {
      console.error('Error saving address:', err);
      setError('حدث خطأ أثناء حفظ العنوان');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (addressId: string) => {
    if (!currentUser) return;
    if (!window.confirm('هل أنت متأكد من رغبتك في حذف هذا العنوان؟')) return;

    try {
      setLoading(true);
      setError(null);
      await deleteAddress(currentUser.id, addressId);
      await fetchAddresses();
    } catch (err) {
      console.error('Error deleting address:', err);
      setError('حدث خطأ أثناء حذف العنوان');
    } finally {
      setLoading(false);
    }
  };

  const handleSetDefault = async (addressId: string) => {
    if (!currentUser) return;
    try {
      setLoading(true);
      setError(null);
      await setDefaultAddress(currentUser.id, addressId);
      await fetchAddresses();
    } catch (err) {
      console.error('Error setting default address:', err);
      setError('حدث خطأ أثناء تعيين العنوان الافتراضي');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
          عناوين الشحن الخاصة بي
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleOpenAdd}
        >
          إضافة عنوان جديد
        </Button>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading && addresses.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : addresses.length === 0 ? (
        <Alert severity="info">
          لا توجد عناوين شحن مسجلة حالياً. قم بإضافة عنوانك لتسهيل عملية الشراء.
        </Alert>
      ) : (
        <Grid container spacing={2}>
          {addresses.map((addr) => (
            <Grid item xs={12} sm={6} key={addr.id}>
              <Card variant="outlined" sx={{ position: 'relative', height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {addr.label}
                    </Typography>
                    {addr.isDefault && (
                      <Chip label="افتراضي" color="primary" size="small" />
                    )}
                  </Box>
                  <Typography variant="body2" color="text.primary" gutterBottom>
                    <strong>الاسم:</strong> {addr.fullName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>الهاتف:</strong> {addr.phone}
                  </Typography>
                  {addr.email && (
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <strong>البريد الإلكتروني:</strong> {addr.email}
                    </Typography>
                  )}
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>العنوان:</strong> {addr.address}
                    {addr.building && `، مبنى: ${addr.building}`}
                    {addr.floor && `، دور: ${addr.floor}`}
                    {addr.apartment && `، شقة: ${addr.apartment}`}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>المدينة:</strong> {addr.city}
                  </Typography>
                  {addr.region && (
                    <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1 }}>
                      منطقة الشحن: {
                        DEFAULT_EGYPT_SHIPPING_REGIONS.find(r => r.id === addr.region)?.name || addr.region
                      }
                    </Typography>
                  )}
                </CardContent>
                <CardActions sx={{ justifyContent: 'flex-end', borderTop: '1px solid #f0f0f0', bgcolor: '#fafafa', p: 1 }}>
                  {!addr.isDefault && (
                    <Button size="small" color="primary" onClick={() => handleSetDefault(addr.id)}>
                      تعيين كافتراضي
                    </Button>
                  )}
                  <Button size="small" color="info" startIcon={<EditIcon />} onClick={() => handleOpenEdit(addr)}>
                    تعديل
                  </Button>
                  <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={() => handleDelete(addr.id)}>
                    حذف
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Dialog: Add/Edit Address */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold' }}>
          {editingAddress ? 'تعديل عنوان الشحن' : 'إضافة عنوان شحن جديد'}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                label="اسم مستعار للعنوان (مثال: المنزل، العمل)"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                label="الاسم الكامل للمستلم"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                label="رقم الهاتف"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="email"
                label="البريد الإلكتروني (اختياري)"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel id="address-city-label">المدينة / المحافظة</InputLabel>
                <Select
                  labelId="address-city-label"
                  value={city}
                  label="المدينة / المحافظة"
                  onChange={(e) => handleCityChange(e.target.value)}
                >
                  {DEFAULT_EGYPT_SHIPPING_REGIONS.map((region) => (
                    <MenuItem key={region.id} value={region.name}>
                      {region.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                multiline
                rows={2}
                label="تفاصيل العنوان (الشارع، اسم المنطقة)"
                value={addressText}
                onChange={(e) => setAddressText(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="رقم المبنى"
                value={building}
                onChange={(e) => setBuilding(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="الدور"
                value={floor}
                onChange={(e) => setFloor(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="الشقة"
                value={apartment}
                onChange={(e) => setApartment(e.target.value)}
              />
            </Grid>
            {region && (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  <strong>منطقة الشحن المحددة تلقائياً: </strong>
                  {DEFAULT_EGYPT_SHIPPING_REGIONS.find(r => r.id === region)?.name || region}
                </Typography>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setOpenDialog(false)} color="inherit">
            إلغاء
          </Button>
          <Button onClick={handleSave} variant="contained" color="primary" disabled={loading}>
            حفظ العنوان
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProfilePage;
